import React, { useEffect, useState } from 'react';
import './RollsRequest.scss';
import Swal from 'sweetalert2';
import { TFunction, withTranslation } from 'react-i18next';
import { ITerminal } from "../../models/ITerminal";
import { TerminalsService } from '../../services/TerminalsService';
import { UserService } from '../../services/UserService';
import { WebCache } from '../../services/WebCache';
import { AxiosResponse } from 'axios';
import EmailField from '../controls/fields/emailField';
import Form from '../containers/form';
import TextField from '../controls/fields/textField';
import { IManagementUser } from '../../models/IUser';
import { ReactComponent as RollRequestSVG } from "../../../node_modules/itd-common/assets/icons/rollrequest.svg";

type Props = {
    terminal?: ITerminal;
    t: TFunction;
    onClose?: () => void;
    onOrderComplete?: (orderId: string) => void;
};

const RollReport: React.FC<Props> = ({ t, onClose, onOrderComplete }) => {
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [stepOneData, setStepOneData] = useState<Record<string, string>>({ phone: '', phonePrefix: '+54911' });
    const [terminals, setTerminals] = useState<ITerminal[]>([]);
    const [selectedTerminals, setSelectedTerminals] = useState<string[]>([]);
    const [terminalFilter, setTerminalFilter] = useState<string>('');
    const [addressFilter, setAddressFilter] = useState<string>('');
    const [orderResult, setOrderResult] = useState<{ orderId: string; createdAt: string } | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [terminalsLoading, setTerminalsLoading] = useState<boolean>(true);
    const [nameError, setNameError] = useState<boolean>(false);

    const filteredTerminals = terminals.filter(term =>
        !term.deleted &&
        term.terminalNumber.toLowerCase().includes(terminalFilter.toLowerCase()) &&
        (term.locationName ?? '').toLowerCase().includes(addressFilter.toLowerCase())
    );

    useEffect(() => {
        const currentUserEmail = WebCache.getCurrentUser()?.email ?? '';
        if (currentUserEmail) {
            UserService.getAll({ Email: currentUserEmail, Profile: [], Skip: 0, Take: 0 })
                .then((response: AxiosResponse<IManagementUser[]>) => {
                    if (response?.data?.length > 0) {
                        const user = response.data[0];
                        setStepOneData(prev => ({
                            ...prev,
                            email: user.email ?? '',
                            name: user.name ?? '',
                            phone: user.phoneNumber ?? '',
                        }));
                    }
                })
                .catch(() => { });
        }
    }, []);

    useEffect(() => {
        TerminalsService.getTerminals({
            TerminalId: '',
            Department: '',
            Location: '',
            Branch: '',
            PayerTaxIds: [],
            Skip: 0,
            Take: 0,
        }).then((response: AxiosResponse<ITerminal[]>) => {
            if (response?.data) {
                const data = response.data.map(t => ({
                    ...t,
                    terminalNumber: t.terminalNumber.replace('B24_', ''),
                }));
                setTerminals(data);
            }
        }).catch(() => { }).finally(() => setTerminalsLoading(false));
    }, []);

    const handleCheckboxChange = (terminalNumber: string) => {
        setSelectedTerminals(prev =>
            prev.includes(terminalNumber)
                ? prev.filter(t => t !== terminalNumber)
                : [...prev, terminalNumber]
        );
    };

    const isStepOneValid =
        !!stepOneData.email?.trim() &&
        !!stepOneData.name?.trim() &&
        !!stepOneData.phonePrefix?.trim() &&
        !!stepOneData.phone?.trim() &&
        !!stepOneData.street?.trim() &&
        !!stepOneData.streetNumber?.trim() &&
        !!stepOneData.postalCode?.trim() &&
        !!stepOneData.province?.trim() &&
        !!stepOneData.scheduleFrom?.trim() &&
        !!stepOneData.scheduleTo?.trim();

    const goToStepTwo = () => setCurrentStep(2);

    const handleSubmit = () => {
        if (submitting) return;
        setSubmitting(true);
        const nameParts = (stepOneData.name ?? '').trim().split(/\s+/);
        const firstName = nameParts[0] ?? '';
        const lastName = nameParts.slice(1).join(' ') || '-';
        TerminalsService.rollOrder({
            terminalIds: selectedTerminals,
            firstName,
            lastName,
            email: stepOneData.email ?? '',
            phone: (stepOneData.phonePrefix ?? '') + (stepOneData.phone ?? ''),
            address: {
                street: stepOneData.street ?? '',
                number: stepOneData.streetNumber ?? '',
                floor: stepOneData.floor || undefined,
                apartment: stepOneData.apartment || undefined,
                zipCode: stepOneData.postalCode ?? '',
                city: stepOneData.province ?? '',
                province: stepOneData.province ?? '',
                businessHours: stepOneData.scheduleFrom && stepOneData.scheduleTo
                    ? `${stepOneData.scheduleFrom} - ${stepOneData.scheduleTo}`
                    : undefined,
            },
        }).then((response: AxiosResponse<{ orderId: string; createdAt: string }>) => {
            if (response.status === 200) {
                const createdAt = new Date(response?.data?.createdAt);
                const formatted = createdAt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                const id = response?.data?.orderId ?? '';
                setOrderResult({ orderId: id, createdAt: formatted });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'No pudimos crear el pedido',
                    text: 'Ocurrió un problema al procesar tu solicitud. Por favor, intentá nuevamente más tarde.',
                    confirmButtonText: 'Ok',
                    confirmButtonColor: '#f60'
                });
            }
        }).catch(() => {
            Swal.fire({
                icon: 'error',
                title: 'No pudimos crear el pedido',
                text: 'Ocurrió un problema al procesar tu solicitud. Por favor, intentá nuevamente más tarde.',
                confirmButtonText: 'Ok',
                confirmButtonColor: '#f60'
            });
        }).finally(() => setSubmitting(false));
    };

    return (
        <div className="detail-payment-report">
            {orderResult ? (
                <div className="dpr-success card">
                    <div className="dpr-success-image-placeholder">
                        <RollRequestSVG />
                    </div>
                    <div className="dpr-success-detail">
                        <h3 className="dpr-success-title mb-4">¡Pedido creado correctamente!</h3>
                        <div className="dpr-success-row">
                            <span className="dpr-success-label">Fecha de carga</span>
                            <span className="dpr-success-value">{orderResult.createdAt}</span>
                        </div>
                        <div className="dpr-success-row">
                            <span className="dpr-success-label">Fecha de entrega</span>
                            <span className="dpr-success-value">Entre 5 y 7 días hábiles</span>
                        </div>
                        <div className="dpr-success-row">
                            <span className="dpr-success-label">Número de pedido</span>
                            <span className="dpr-success-value">{orderResult.orderId}</span>
                        </div>
                        <div className="dpr-success-row">
                            <span className="dpr-success-label">Estado</span>
                            <span className="roll-status roll-status--pending">Pedido generado</span>
                        </div>
                    </div>
                    <div className="dpr-step-actions">
                        <div className="col-md-2"></div>
                        <button className="col-md-8 buttonStepNext" onClick={() => {
                            const orderId = orderResult?.orderId ?? '';
                            setOrderResult(null);
                            setCurrentStep(1);
                            setSelectedTerminals([]);
                            setStepOneData({ phone: '', phonePrefix: '+54911' });
                            onOrderComplete?.(orderId);
                            onClose?.();
                        }}>
                            <span className="textNewReport">Volver</span>
                        </button>
                        <div className="col-md-2"></div>
                    </div>
                </div>
            ) : (
                <React.Fragment>
                    <div className="dpr-progress-container">
                        <div className="dpr-progress-bars">
                            <div className={`dpr-progress-bar ${currentStep >= 1 ? 'active' : 'inactive'}`}></div>
                            <div className={`dpr-progress-bar ${currentStep >= 2 ? 'active' : 'inactive'}`}></div>
                        </div>
                        <span className="dpr-progress-label">{currentStep === 1 ? 'Paso 1' : 'Paso 2'}</span>
                    </div>
                    {currentStep === 1 && (
                        <div className="dpr-step-one card">
                            <h3 className="dpr-step-one-title">
                                Confirmá o actualizá tus datos de
                                <br />
                                contacto por favor
                            </h3>
                            <Form model={[stepOneData, setStepOneData]}>
                                <div className="dpr-step-one-grid">
                                    <div className="dpr-step-one-row">
                                        <span className="dpr-section-label-required">Email</span>
                                        <EmailField
                                            attr="email"
                                            required
                                            autoFocus
                                        ></EmailField>
                                    </div>
                                    <div className="dpr-step-one-row">
                                        <span className="dpr-section-label-required">Nombre y apellido</span>
                                        <TextField
                                            attr="name"
                                            required
                                            onChange={(e) => {
                                                const filtered = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s'\-.]/g, '');
                                                setNameError(filtered !== e.target.value);
                                                e.target.value = filtered;
                                            }}
                                        ></TextField>
                                        {nameError && (
                                            <span style={{ color: 'red', fontSize: '0.75rem', marginTop: '2px' }}>
                                                No se permiten caracteres especiales (ej: @, #, $, %...)
                                            </span>
                                        )}
                                    </div>
                                    <div className="dpr-step-one-row">
                                        <span className="dpr-section-label-required">Número de teléfono</span>
                                        <div className="dpr-phone-row">
                                            <div className="dpr-phone-prefix">
                                                <TextField
                                                    attr="phonePrefix"
                                                    required
                                                ></TextField>
                                            </div>
                                            <div className="dpr-phone-number">
                                                <TextField
                                                    attr="phone"
                                                    required
                                                ></TextField>
                                            </div>
                                        </div>
                                        <span className="dpr-field-hint">*Completar el número sin el 15</span>
                                    </div>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="396" height="2" viewBox="0 0 396 2" fill="none">
                                        <path d="M0 1H396" stroke="#EBEBEB" stroke-width="2" />
                                    </svg>
                                    <div className="dpr-step-one-row">
                                        <div className="dpr-phone-row dpr-fields-equal">
                                            <div className="dpr-field-col">
                                                <span className="dpr-section-label-required">Calle</span>
                                                <TextField
                                                    attr="street"
                                                    required
                                                ></TextField>
                                            </div>
                                            <div className="dpr-field-col" style={{ flex: '0 0 120px' }}>
                                                <span className="dpr-section-label-required">Número</span>
                                                <TextField
                                                    attr="streetNumber"
                                                    required
                                                ></TextField>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dpr-step-one-row">
                                        <div className="dpr-phone-row dpr-fields-equal">
                                            <div className="dpr-field-col">
                                                <span className="dpr-section-label">Piso</span>
                                                <TextField
                                                    attr="floor"
                                                ></TextField>
                                            </div>
                                            <div className="dpr-field-col">
                                                <span className="dpr-section-label">Dpto</span>
                                                <TextField
                                                    attr="apartment"
                                                ></TextField>
                                            </div>
                                            <div className="dpr-field-col">
                                                <span className="dpr-section-label-required">CP</span>
                                                <TextField
                                                    attr="postalCode"
                                                    required
                                                ></TextField>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dpr-step-one-row">
                                        <span className="dpr-section-label-required">Localidad</span>
                                        <TextField
                                            attr="province"
                                            required
                                        ></TextField>
                                    </div>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="396" height="2" viewBox="0 0 396 2" fill="none">
                                        <path d="M0 1H396" stroke="#EBEBEB" stroke-width="2" />
                                    </svg>
                                    <span className="dpr-section-label-required">Horario de atención</span>
                                    <div className="dpr-schedule-row">
                                        <div className="dpr-schedule-field">
                                            <span className="dpr-field-sublabel">Desde</span>
                                            <input
                                                type="time"
                                                className="dpr-time-input"
                                                value={stepOneData.scheduleFrom ?? ''}
                                                onChange={e => setStepOneData(prev => ({ ...prev, scheduleFrom: e.target.value }))}
                                                required
                                            />
                                        </div>
                                        <div className="dpr-schedule-spacer"></div>
                                        <div className="dpr-schedule-field">
                                            <span className="dpr-field-sublabel">Hasta</span>
                                            <input
                                                type="time"
                                                className="dpr-time-input"
                                                value={stepOneData.scheduleTo ?? ''}
                                                onChange={e => setStepOneData(prev => ({ ...prev, scheduleTo: e.target.value }))}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                            <div className="dpr-step-actions">
                                <button className='col-md-12 buttonStepNext' onClick={goToStepTwo} disabled={!isStepOneValid}><span className='textNewReport'>Continuar</span></button>
                            </div>
                        </div>
                    )}
                    {currentStep === 2 && !terminalsLoading && terminals.length > 0 && (
                        <div className="dpr-terminals card">
                            <h3 className="dpr-step-one-title">
                                Seleccioná las terminales para las cuales
                                <br />
                                harás el pedido
                            </h3>
                            <div className="dpr-terminal-filter-wrapper">
                                <input
                                    type="text"
                                    className="dpr-terminal-filter"
                                    placeholder="Buscá tu terminal"
                                    value={terminalFilter}
                                    onChange={e => setTerminalFilter(e.target.value)}
                                />
                            </div>
                            <div className="dpr-terminal-filter-wrapper">
                                <input
                                    type="text"
                                    className="dpr-terminal-filter"
                                    placeholder="Buscá tu dirección"
                                    value={addressFilter}
                                    onChange={e => setAddressFilter(e.target.value)}
                                />
                            </div>
                            <ul className="dpr-terminals-list">
                                {filteredTerminals.length === 0 && (
                                    <li className="dpr-terminal-no-results">Sin resultados de su búsqueda</li>
                                )}
                                {filteredTerminals.map(term => {
                                    const isSelected = selectedTerminals.includes(term.terminalNumber);
                                    return (
                                        <li key={term.terminalNumber} className={`dpr-terminal-item${isSelected ? ' dpr-terminal-item--selected' : ''}`}>
                                            <label className="dpr-terminal-label">
                                                <input
                                                    type="checkbox"
                                                    className="dpr-terminal-checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleCheckboxChange(term.terminalNumber)}
                                                />
                                                <div className="dpr-terminal-data">
                                                    <div className="col-md-12">
                                                        <span className="dpr-terminal-field-label">{t('terminalnro')} </span>
                                                        <span className="dpr-terminal-field-value">{term.terminalNumber}</span>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <span className="dpr-terminal-field-value dpr-terminal-field-value--light">{term.locationName || "-"}</span>
                                                    </div>
                                                </div>
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className="dpr-step-actions">
                                <button className='col-md-12 buttonStepNext' onClick={handleSubmit} disabled={selectedTerminals.length === 0 || submitting}><span className='textNewReport'>{submitting ? 'Procesando...' : 'Hacer pedido'}</span></button>
                            </div>
                        </div>
                    )}
                    {currentStep === 2 && terminalsLoading && (
                        <div className="dpr-terminals card">
                            <span className="dpr-terminal-field-label">Cargando terminales...</span>
                        </div>
                    )}
                    {currentStep === 2 && !terminalsLoading && terminals.length === 0 && (
                        <div className="dpr-terminals card">
                            <span className="dpr-terminal-field-label">Sin terminales para mostrar.</span>
                        </div>
                    )}
                </React.Fragment>
            )}
        </div>
    );
};

export default withTranslation()(RollReport);
